import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { isWatchlistMatch } from '../utils/watchlistMatching';
import { subscribeToNewsletter, PENDING_NEWSLETTER_KEY } from '../utils/newsletterSignup';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [watchlist, setWatchlist] = useState([]);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        fetchWatchlist(session.user.id);
        completePendingNewsletterOptIn(session.user);
      } else {
        setProfile(null);
        setWatchlist([]);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Completes a newsletter opt-in that was ticked on the signup form before a
  // Google OAuth redirect (the box's state can't survive the round-trip any
  // other way). Three guards, each earned by a QA failure:
  //  - only for a user DIFFERENT from whoever was signed in when the box was
  //    ticked (9/2: the old session's own auth events consumed a bare flag and
  //    subscribed the wrong account);
  //  - only for accounts created in the last 15 minutes, so a stale flag from
  //    an abandoned signup can never subscribe a returning user;
  //  - the flag itself expires after 15 minutes.
  function completePendingNewsletterOptIn(sessionUser) {
    try {
      const raw = localStorage.getItem(PENDING_NEWSLETTER_KEY);
      if (!raw) return;
      let flag = null;
      try { flag = JSON.parse(raw); } catch { /* legacy/garbage value */ }
      if (!flag || !flag.setAt || Date.now() - flag.setAt > 15 * 60 * 1000) {
        localStorage.removeItem(PENDING_NEWSLETTER_KEY);
        return;
      }
      if (sessionUser.id === flag.byUserId) return; // the OLD session — leave the flag for the new one
      localStorage.removeItem(PENDING_NEWSLETTER_KEY);
      const createdAt = new Date(sessionUser.created_at).getTime();
      if (!createdAt || Date.now() - createdAt > 15 * 60 * 1000) return;
      if (sessionUser.email) subscribeToNewsletter(sessionUser.email).catch(() => {});
    } catch { /* storage unavailable — nothing to complete */ }
  }

  async function fetchProfile(userId) {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    setProfile(data);
  }

  async function fetchWatchlist(userId) {
    const { data } = await supabase
      .from('watchlist')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    setWatchlist(data || []);
  }

  async function signInWithGoogle() {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin }
    });
    if (error) throw error;
  }

  async function signInWithEmail(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }

  async function signUpWithEmail(email, password) {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin }
    });
    if (error) throw error;
  }

  async function signOut() {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setWatchlist([]);
  }

  async function addToWatchlist(movieTitle, movieId = null, tmdbId = null) {
    if (!user) return;
    const { data, error } = await supabase
      .from('watchlist')
      .insert([{ user_id: user.id, movie_title: movieTitle, movie_id: movieId, tmdb_id: tmdbId, source: 'manual' }])
      .select()
      .single();
    if (error) {
      if (error.code === '23505') return; // Already exists
      throw error;
    }
    setWatchlist(prev => [data, ...prev]);
  }

  async function removeFromWatchlist(movieTitle) {
    if (!user) return;
    await supabase
      .from('watchlist')
      .delete()
      .eq('user_id', user.id)
      .eq('movie_title', movieTitle);
    setWatchlist(prev => prev.filter(w => w.movie_title !== movieTitle));
  }

  function isOnWatchlist(movieTitle, movieId = null) {
    return watchlist.some(w => isWatchlistMatch({
      watchlistMovieId: w.movie_id,
      watchlistTitle: w.movie_title,
      showMovieId: movieId,
      showTitle: movieTitle,
    }));
  }

  async function updateProfile(updates) {
    if (!user) return;
    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id);
    if (error) throw error;
    setProfile(prev => ({ ...prev, ...updates }));
  }

  // Saved filters
  const [savedFilters, setSavedFilters] = useState([]);

  async function fetchSavedFilters(userId) {
    const { data } = await supabase
      .from('saved_filters')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    setSavedFilters(data || []);
  }

  async function saveFilter(name, filters) {
    if (!user) return;
    const { data, error } = await supabase
      .from('saved_filters')
      .upsert([{ user_id: user.id, filter_name: name, ...filters }], { onConflict: ['user_id', 'filter_name'] })
      .select()
      .single();
    if (!error && data) {
      setSavedFilters(prev => {
        const existing = prev.findIndex(f => f.filter_name === name);
        if (existing >= 0) { const updated = [...prev]; updated[existing] = data; return updated; }
        return [data, ...prev];
      });
    }
  }

  async function deleteFilter(id) {
    await supabase.from('saved_filters').delete().eq('id', id);
    setSavedFilters(prev => prev.filter(f => f.id !== id));
  }

  // Fetch saved filters on login
  useEffect(() => {
    if (user) fetchSavedFilters(user.id);
  }, [user]);

  const value = {
    user,
    profile,
    loading,
    watchlist,
    savedFilters,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    addToWatchlist,
    removeFromWatchlist,
    isOnWatchlist,
    updateProfile,
    fetchWatchlist,
    saveFilter,
    deleteFilter,
    supabase,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
